import React, { useRef } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';

import { Flex } from '@rover/kibble/core';
import { SitterProfileGalleryNavigationEvent } from '@rover/react-lib/src/pages/sitter-profile/SitterProfilePage/analyticsEvents';
import type { Image as ImageType } from '@rover/rsdk/src/apiClient/latest/model/image';
import emitAnalyticsEvent from '@rover/rsdk/src/modules/Analytics/emitAnalyticsEvent';
import { Logger } from '@rover/rsdk/src/modules/Logging';
import emitMetric from '@rover/utilities/emitMetric';

import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/captions.css';

type Props = {
  images: ImageType[];
  index: number;
  handleOnCloseLightbox: () => void;
  onReachEnd: () => void;
  isInSitterGalleryCropFixExperiment?: boolean;
};

const PhotoGallerySlider = ({
  images,
  index,
  handleOnCloseLightbox,
  onReachEnd,
  isInSitterGalleryCropFixExperiment = false,
}: Props): JSX.Element => {
  const currentIndexRef = useRef<number>(index);
  const moveNext = (currentIndex: number): void => {
    emitMetric('sitter_profile_gallery_navigation', { direction: 'next' });
    Logger.info('sitter_profile_gallery_navigation', { direction: 'next' });
    emitAnalyticsEvent(
      new SitterProfileGalleryNavigationEvent({
        direction: 'next',
        inCropFix: isInSitterGalleryCropFixExperiment,
      })
    );
    if (currentIndex + 1 === images.length - 1) {
      onReachEnd();
    }
  };

  const movePrev = (): void => {
    emitMetric('sitter_profile_gallery_navigation', { direction: 'previous' });
    emitAnalyticsEvent(
      new SitterProfileGalleryNavigationEvent({
        direction: 'previous',
        inCropFix: isInSitterGalleryCropFixExperiment,
      })
    );
    Logger.info('sitter_profile_gallery_navigation', { direction: 'previous' });
  };
  return (
    <Flex flexDirection="row" width="100%" data-testid="photo-gallery-slider">
      <Lightbox
        open
        index={currentIndexRef.current}
        close={handleOnCloseLightbox}
        slides={images.map((i) => ({
          src: i.largeUncroppedRetina || i.largeUncropped,
          description: i.caption,
          alt: i.caption,
        }))}
        plugins={[Zoom, Captions]}
        zoom={{ scrollToZoom: true }}
        carousel={{ finite: true }}
        controller={{ closeOnBackdropClick: true }}
        on={{
          view: ({ index: currentIndex }) => {
            const previousIndex = currentIndexRef.current;
            currentIndexRef.current = currentIndex;

            if (previousIndex === undefined || previousIndex === currentIndex) return;

            if (currentIndex > previousIndex) {
              moveNext(currentIndex);
            } else if (currentIndex < previousIndex) {
              movePrev();
            }
          },
        }}
      />
    </Flex>
  );
};

export default PhotoGallerySlider;
